import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ScanMode } from '@/lib/api';
import { toast } from 'sonner';

interface TriggerAnalysisResponse {
  message: string;
  run_id: string;
  scan_mode?: ScanMode;
}

export const useTriggerAnalysis = () => {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  // Debounce invalidation to prevent multiple refetches when multiple scans complete
  // We use a module-level or static-like approach, but within the hook we can use a ref if we want instance-level
  // However, the scanning logic might be scattered (though unlikely). 
  // Let's rely on React Query's built-in coalescing, but explicitly debouncing the invalidate call is safer for "bulk" status updates.
  


  // Simple debounce wrapper could be implemented here or outside.
  // Given we are inside a hook, let's just call it directly.
  // The user requested: "Implement a debounced invalidation or a 'summary refetch' for bulk scans."
  // Since `pollAnalysisStatus` is async and independent, we need a way to coalesce these calls.
  // We'll use a module-level debounce helper to ensure all hook instances share the timer if necessary,
  // or just rely on the fact that the modal is a single component instance.
  // But wait, `pollAnalysisStatus` is a closure.
  
  const pollAnalysisStatus = async (runId: string, repositoryId?: string, repositoryName?: string, onSuccess?: () => void) => {
    const maxAttempts = 60; // 5 minutes
    let attempts = 0;

    const checkStatus = async () => {
      if (attempts >= maxAttempts) {
        toast.error(`Analysis for ${repositoryName || 'repository'} timed out.`);
        return;
      }

      try {
        const response = await api.get(`/api/analysis-runs/${runId}`);
        const run = response.data;

        if (run.status === 'completed') {
          toast.success(`Analysis for ${repositoryName || 'repository'} completed successfully.`);
          
          // Invalidate queries
          // Note: Logic for debounce should ideally be here if we want to batch them.
          // For now, calling it directly as React Query handles some deduping, 
          // but to strictly follow the directive "Implement a debounced invalidation":
          debouncedInvalidate(queryClient, repositoryId);
          
          if (onSuccess) onSuccess();
        } else if (run.status === 'failed') {
          toast.error(`Analysis for ${repositoryName || 'repository'} failed.`);
        } else {
          attempts++;
          setTimeout(checkStatus, 5000);
        }
      } catch (error) {
        console.error('Polling error:', error);
        attempts++;
        setTimeout(checkStatus, 5000);
      }
    };

    checkStatus();
  };

  // scanMode is sent only when the user chose one; omitted, the API runs its
  // default incremental scan.
  const triggerAnalysis = async (repositoryId: string, repositoryName?: string, onSuccess?: () => void, scanMode?: ScanMode) => {
    setIsLoading(true);
    try {
      const response = await api.post<TriggerAnalysisResponse>('/api/analysis/trigger', {
        repository_id: repositoryId,
        ...(scanMode ? { scan_mode: scanMode } : {}),
      });

      toast.info(scanMode === 'full'
        ? `Full repository scan for ${repositoryName || 'repository'} has been triggered successfully.`
        : `Analysis for ${repositoryName || 'repository'} has been triggered successfully.`);

      if (response.data.run_id) {
        pollAnalysisStatus(response.data.run_id, repositoryId, repositoryName, onSuccess);
      }

      return response.data;
    } catch (error: any) {
      console.error('Failed to trigger analysis:', error);

      if (error?.data?.code === 'LIMIT_REACHED_SCANS') {
        toast.error(error.data.error || 'You have reached your monthly scan limit.', {
          duration: 6000,
        });
      } else {
        toast.error(error.message || 'Failed to trigger analysis.');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    triggerAnalysis,
    isLoading,
  };
};

// Module-level debounce to share across scans
let debounceTimer: NodeJS.Timeout | null = null;
const debouncedInvalidate = (queryClient: any, repositoryId?: string) => {
    if (debounceTimer) {
        clearTimeout(debounceTimer);
    }
    
    debounceTimer = setTimeout(() => {
        // Core invalidation logic
        const globalKeys = [
          ['dashboardStats'],
          ['issues'],
          ['analysisRuns'],
          ['repositoryStats'],
          ['dashboardEvolution'],
      ];

      globalKeys.forEach(key => {
          queryClient.invalidateQueries({ queryKey: key });
      });

      if (repositoryId) {
          // This part is tricky with debounce if we have multiple repo IDs. 
          // A true bulk invalidation might drop specific IDs if we just take the last one.
          // However, for "Evolution" and global stats, it doesn't matter. 
          // For specific repo details, we might want to collect them or just invalidate all.
          // The prompt specifically asks for `dashboardEvolution` and "summary refetch".
          // We will invalidate the *specific* repo immediately or accumulate them?
          // Simplest safe approach: Invalidate global ONLY in debounce, and specific immediately?
          // Or just run it. If 5 finish at once, the last one wins the debounce.
          // Actually, we should accumulate IDs or just assume "something changed" and invalidate lists.
          // Let's stick to the prompt's focus: "Invalidate analysis queries"
          // We'll keep it simple: run the full invalidation. 
          // If we lose specific repo ID invalidation for previous ones in the batch, 
          // those specific repo pages might be stale if the user is looking at them *right now*.
          // But usually they are on the dashboard.
          // Let's separate global and specific.
      }
      debounceTimer = null;
    }, 2000); // 2 second window

    // Immediate specific invalidation (optional, but safer for drilled-down views)
    if (repositoryId) {
        queryClient.invalidateQueries({ queryKey: ['repository', repositoryId] });
        queryClient.invalidateQueries({ queryKey: ['repositoryDetails', repositoryId] });
        queryClient.invalidateQueries({ queryKey: ['repositoryIssues', repositoryId] });
        queryClient.invalidateQueries({ queryKey: ['repositorySnapshots', repositoryId] });
        queryClient.invalidateQueries({ queryKey: ['repositoryHotspots', repositoryId] });
    }
};
